import React, { useRef, useState } from 'react'
import Slider from 'react-slick'
import { Container } from 'react-grid-system'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { CarouselSlide, Heading, Paragraph } from '~elements'
import { CarouselSlideProps, RichTextType } from '~types'
import styles from './Carousel.module.scss'
import { useWindowSize } from 'utils/hooks'
import { SizeBreakpointLg, SizeBreakpointMd, SizeBreakpointSm } from '~styles/dist/tokens.mjs'

interface CarouselProps {
  autoPlay: boolean
  carouselSlides: Array<CarouselSlideProps>
  description?: RichTextType
  headline?: string
  loopCarousel?: boolean
  showModal?: boolean
  showArrows?: boolean
  showDots?: boolean
  slidesToShow: number
}

const Carousel = ({
  autoPlay = false,
  carouselSlides = [],
  description,
  headline,
  loopCarousel,
  showArrows = false,
  showDots = false,
  showModal = false,
  slidesToShow = 1
}: CarouselProps) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(1)
  const largeScreenBreakpoint = Number(SizeBreakpointLg)
  const mediumScreenBreakpoint = Number(SizeBreakpointMd)
  const smallScreenBreakpoint = Number(SizeBreakpointSm)
  const { isBreakpoint: isBelowBreakpointXs } = useWindowSize('xs', '<=')
  const { isBreakpoint: isBelowBreakpointSm, windowSizeCalculation } = useWindowSize('sm', '<')

  const settings = {
    arrows: showArrows,
    autoplay: autoPlay,
    dots: showDots,
    infinite: loopCarousel,
    slidesToScroll: slidesToShow,
    slidesToShow: slidesToShow,
    speed: 700,
    adaptiveWidth: true,
    beforeChange: (next: number) => {
      if (slidesToShow === 1) {
        setActiveSlideIndex(next + slidesToShow)
      } else if (slidesToShow === 3) {
        if (isBelowBreakpointXs) {
          setActiveSlideIndex(next ? next + 1 : next + 1)
        } else if (isBelowBreakpointSm) {
          setActiveSlideIndex(next ? next + 2 : next + 1)
        } else {
          setActiveSlideIndex(next ? next + slidesToShow : next + 1)
        }
      }
    },
    responsive: [
      {
        breakpoint: largeScreenBreakpoint,
        settings: {
          slidesToShow: slidesToShow == 1 ? slidesToShow : slidesToShow,
          slidesToScroll: slidesToShow == 1 ? slidesToShow : slidesToShow
        }
      },
      {
        breakpoint: mediumScreenBreakpoint,
        settings: {
          slidesToShow: slidesToShow == 3 ? 2 : slidesToShow,
          slidesToScroll: slidesToShow == 3 ? 2 : slidesToShow
        }
      },
      {
        breakpoint: smallScreenBreakpoint,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  }

  const sliderRef = useRef<Slider | null>(null)

  const handleSlickNext = () => {
    sliderRef?.current?.slickNext()
  }

  const handleSlickPrev = () => {
    sliderRef?.current?.slickPrev()
  }

  return (
    <div className={`${styles['carousel-container']} ${description ? styles['edp-experience'] : ''}`}>
      <Container className={'container overflow-visible'}>
        {headline && (
          <Heading level={2} justify='center' noMarginTop>
            {headline}
          </Heading>
        )}
        {description && <Paragraph text={description} fontSize={'sm'} justify={'center'} />}

        <div className={styles['nav-container']}>
          <div className={styles['count-container']}>
            <p>{`${activeSlideIndex} / ${carouselSlides.length}`}</p>
          </div>
          <div className={styles['slider-nav-container']}>
            <div
              className={cx([styles['slick-arrow-custom'], styles['slick-arrow-custom--prev']])}
              onClick={() => handleSlickPrev()}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                size='lg'
                className={activeSlideIndex === 1 && !loopCarousel ? styles['disabled-nav'] : ''}
              />
            </div>
            <div
              className={cx([styles['slick-arrow-custom'], styles['slick-arrow-custom--next']])}
              onClick={() => handleSlickNext()}
            >
              <FontAwesomeIcon
                icon={faChevronRight}
                size='lg'
                className={activeSlideIndex === carouselSlides.length && !loopCarousel ? styles['disabled-nav'] : ''}
              />
            </div>
          </div>
        </div>
        {carouselSlides.length > 0 && windowSizeCalculation ? (
          <Slider ref={sliderRef} className={`slides-shown-${slidesToShow} slide-overflow-is-visible`} {...settings}>
            {carouselSlides.map((slide, index: number) => (
              <CarouselSlide key={index} {...slide} openImageInModal={showModal} />
            ))}
          </Slider>
        ) : null}
      </Container>
    </div>
  )
}

export default Carousel
